/**
 * RecordDetail - Expanded view for a single record
 *
 * Atomic level: Organism
 * Composes: DimensionLabelGroup, DetectionIndicator, Collapsible
 *
 * Layout (matching Figma design):
 * - Gray background with padding
 * - Top row: Left card (verbatim + thread) + Right card (business context)
 * - Conduct Labels card
 * - Detection card
 * - Investigation card
 */

import React, { useState } from 'react';
import { cn } from '../../../../lib/utils';
import { Button } from '../../../ui/button';
import { DimensionLabelGroup } from './DimensionLabelGroup';
import { ConductScoreIndicator } from './ConductScoreIndicator';
import { ProcessingStatusBadge } from './ProcessingStatusBadge';
import { formatTaxonomyLabel } from './taxonomyLabelFormatting';
import IconWrapper from '../../../ui/IconWrapper';
import { useAccess, Capability } from '../../../../hooks/useAccess';
import {
  FiChevronDown,
  FiChevronRight,
  FiRefreshCw,
  FiMoreVertical,
} from 'react-icons/fi';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import type { Record as ConductRecord, VulnerabilityIndicator, DynamicCategoryLabels, TaxonomyLabel } from '../../../../types/record';

interface RecordDetailProps {
  record: ConductRecord;
  vulnerabilityDetectionEnabled?: boolean;
  onReclassify?: (recordId: string) => void;
  isReclassifying?: boolean;
  className?: string;
}


/**
 * Convert dynamic category labels to the groups format for DimensionLabelGroup.
 * Categories are displayed in the order they appear in the data.
 */
const categoriesToGroups = (categories: DynamicCategoryLabels): { name: string; labels: TaxonomyLabel[] }[] => {
  return Object.entries(categories).map(([categoryId, labels]) => {
    // Use categoryName from first label if available, otherwise format the categoryId
    const displayName = labels[0]?.categoryName || formatTaxonomyLabel(categoryId);
    return {
      name: displayName,
      labels,
    };
  });
};

const RecordDetail: React.FC<RecordDetailProps> = ({
  record,
  vulnerabilityDetectionEnabled = true,
  onReclassify,
  isReclassifying = false,
  className,
}) => {
  const [showThread, setShowThread] = useState(false);
  const [showVerbatim, setShowVerbatim] = useState(false);
  const [vulnerabilityDetectionOpen, setVulnerabilityDetectionOpen] = useState(true);
  const { can } = useAccess();
  const canReclassify = can(Capability.MonitoringContribute);

  return (
    <div className={cn('w-full bg-card', className)}>
      <div className="border-b border-border/70 px-4 py-5 sm:px-6 sm:py-6">
        <div className="min-w-0 space-y-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
              <ConductScoreIndicator
                score={record.conductScore}
                size="medium"
                className="flex-shrink-0"
                breakdown={record.conductScoreBreakdown}
              />
              <div className="min-w-0 flex-1 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <h2 className="text-base font-semibold text-foreground sm:text-lg">
                    {record.summary}
                  </h2>
                  <div className="flex shrink-0 items-center gap-2">
                    {(record.processing_status || record._progress?.stage) && (
                      <ProcessingStatusBadge record={record} />
                    )}
                    {onReclassify && canReclassify && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="size-8">
                            <IconWrapper icon={FiMoreVertical} className="size-4" />
                            <span className="sr-only">Actions</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => onReclassify(record.id)}
                            disabled={isReclassifying}
                          >
                            <IconWrapper
                              icon={FiRefreshCw}
                              className={cn('mr-2 size-4', isReclassifying && 'animate-spin')}
                            />
                            {isReclassifying ? 'Reclassifying...' : 'Reclassify'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2 text-sm text-muted-foreground sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
                  <div className="flex items-center gap-2">
                    <span className="whitespace-nowrap">Customer:</span>
                    <span className="font-medium text-foreground">{record.customerName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="whitespace-nowrap">Date:</span>
                    <span className="font-medium text-foreground">
                      {new Date(record.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {(record.summaryDetailed || record.verbatim) && (
              <div className="border-t border-border/70 pt-5">
                <div className="mb-3 flex items-center justify-between gap-4">
                  <h4 className="text-sm font-medium text-muted-foreground">
                    {showVerbatim ? 'Original Text' : 'Analysis Summary'}
                  </h4>
                  {record.summaryDetailed && record.verbatim && (
                    <button
                      type="button"
                      onClick={() => setShowVerbatim(!showVerbatim)}
                      className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {showVerbatim ? 'Show Summary' : 'Show Original'}
                    </button>
                  )}
                </div>
                {showVerbatim && record.verbatim ? (
                  <div className="max-h-[500px] overflow-y-auto text-sm leading-relaxed text-foreground whitespace-pre-wrap" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                    {record.verbatim}
                  </div>
                ) : record.summaryDetailed ? (
                  <div className="space-y-3">
                    {record.summaryDetailed.split('\n\n').map((paragraph, i) => (
                      <p key={i} className="text-sm leading-relaxed text-foreground whitespace-pre-wrap" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                ) : null}
              </div>
            )}

            {record.customerThread && record.customerThread.messages.length > 0 && (
              <div className="border-t border-border/70 pt-5">
                <div className="mb-4 flex items-center justify-between gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-foreground">Customer Communication</h4>
                    <p className="text-xs text-muted-foreground">
                      {record.customerThread.messageCount} {record.customerThread.messageCount === 1 ? 'message' : 'messages'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowThread(!showThread)}
                    className="h-6 px-2 text-xs text-primary transition-colors hover:text-primary/80 hover:underline"
                  >
                    {showThread ? 'Collapse' : 'Expand'}
                  </button>
                </div>

                {showThread && (
                  <div className="flex flex-col gap-4">
                    {record.customerThread.messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={cn(
                          'flex max-w-[85%] gap-3',
                          msg.isCustomer ? 'self-start' : 'self-end flex-row-reverse'
                        )}
                      >
                        <div
                          className={cn(
                            'flex size-9 flex-shrink-0 items-center justify-center rounded-full border text-xs font-medium',
                            msg.isCustomer
                              ? 'border-primary/20 bg-primary/10 text-primary'
                              : 'border-border bg-muted text-muted-foreground'
                          )}
                        >
                          {msg.authorInitials}
                        </div>

                        <div
                          className={cn(
                            'flex min-w-0 flex-col gap-1',
                            msg.isCustomer ? 'items-start' : 'items-end'
                          )}
                        >
                          <div
                            className={cn(
                              'flex flex-wrap items-center gap-2',
                              msg.isCustomer ? 'flex-row' : 'flex-row-reverse'
                            )}
                          >
                            <span className="text-sm font-medium text-foreground">
                              {msg.author}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {msg.timestamp}
                            </span>
                          </div>
                          <div
                            className={cn(
                              'max-w-full rounded-2xl border px-4 py-3',
                              msg.isCustomer
                                ? 'rounded-tl-sm border-primary/10 bg-primary/5'
                                : 'rounded-tr-sm border-border bg-muted/60'
                            )}
                          >
                            <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                              {msg.content}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
        </div>
      </div>

      <section className="border-b border-border/70 px-4 py-5 sm:px-6 sm:py-6">
        <div className="space-y-5">
          <h4 className="text-base font-semibold text-foreground sm:text-lg">
            Conduct Labels
          </h4>
          <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <DimensionLabelGroup
              title="Dissatisfaction"
              variant="dissatisfaction"
              groups={categoriesToGroups(record.conductLabels.complaintDetection)}
              emptyMessage="No dissatisfaction indicators"
            />
            <DimensionLabelGroup
              title="Customer Strain"
              variant="strain"
              groups={[
                { name: 'Emotional/Stress Indicators', labels: record.conductLabels.customerStrain.labels },
              ]}
              emptyMessage="No strain indicators"
            />
            <DimensionLabelGroup
              title="Foreseeable Harm"
              variant="harm"
              groups={categoriesToGroups(record.conductLabels.foreseeableHarm)}
              emptyMessage="No harm indicators"
            />
            <DimensionLabelGroup
              title="Consumer Duty"
              variant="consumer-duty"
              groups={categoriesToGroups(record.conductLabels.consumerDuty)}
              emptyMessage="No consumer duty indicators"
            />
          </div>
        </div>
      </section>

      {vulnerabilityDetectionEnabled && (
        <section className="border-b border-border/70 px-4 py-5 sm:px-6 sm:py-6">
          <div className="space-y-4">
            <h4 className="text-base font-semibold text-foreground sm:text-lg">
              Regulatory Flags
            </h4>

            {vulnerabilityDetectionEnabled && (
              <div className="overflow-hidden rounded-xl border border-border/70 bg-muted/20">
                <div className="flex min-h-14 items-center justify-between px-4 py-3 sm:px-5">
                  <h5 className="text-sm font-medium text-foreground sm:text-base">
                    Vulnerability Detection
                  </h5>
                  <button
                    onClick={() => setVulnerabilityDetectionOpen(!vulnerabilityDetectionOpen)}
                    className="ml-2 flex size-5 flex-shrink-0 items-center justify-center"
                    aria-label={vulnerabilityDetectionOpen ? 'Collapse Vulnerability Detection' : 'Expand Vulnerability Detection'}
                    title={vulnerabilityDetectionOpen ? 'Collapse Vulnerability Detection' : 'Expand Vulnerability Detection'}
                  >
                    <IconWrapper
                      icon={vulnerabilityDetectionOpen ? FiChevronDown : FiChevronRight}
                      className="size-5 text-muted-foreground"
                    />
                  </button>
                </div>
                {vulnerabilityDetectionOpen && (
                  <div className="border-t border-border/70 bg-card px-4 pb-4 pt-4 sm:px-5 sm:pb-5">
                    <DimensionLabelGroup
                      title=""
                      variant="vulnerability"
                      groups={
                        record.vulnerabilityDetection?.detected
                          ? Object.entries(
                              (record.vulnerabilityDetection?.indicators ?? []).reduce<{ [key: string]: VulnerabilityIndicator[] }>((acc, indicator) => {
                                const category = formatTaxonomyLabel(indicator.category);
                                (acc[category] = acc[category] || []).push(indicator);
                                return acc;
                              }, {})
                            ).map(([category, indicators]) => ({
                              name: category,
                              labels: indicators.map((ind) => ({
                                id: ind.label,
                                rationale: ind.rationale || ind.description,
                                regulation_reference: ind.regulation_reference,
                                confidence: ind.confidence,
                              })),
                            }))
                          : []
                      }
                      emptyMessage="No vulnerability indicators detected"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export { RecordDetail };
